import React, { memo, useEffect, useMemo } from 'react'
import fileDownload from 'js-file-download'
import { useAppDispatch } from '../../../../../../../hooks/useAppDispatch'
import { useAppSelector } from '../../../../../../../hooks/useAppSelector'
import { selectRuleIsChecked } from '../../../../../../../store/selectors/ruleCardSelectors'
import { inputValidationCheck } from '../../../../../../../validators/inputValidators/inputValidators'
import { PATH_JOINER } from '../../../../../utils/getConfigBitPath'
import { useFormValidation } from '../../../../../../../hooks/useFormValidation'
import { getLabel } from '../../../../../../../utils/getLabel'
import { CsvDataImporterArgs } from '../../../../../../modals/CsvModal/CsvModal'
import { getErrorsByType } from '../../../../../control'
import { showModal } from '../../../../../../../store/actions/modalActions'
import { ModalTypes } from '../../../../../../../types/modals'
import { mapToDictArr } from '../../../../../../overlays/DataImportExportCsv/mapToDictArr'
import { exportDataToCsv } from '../../../../../../../utils/exportDataToCsv'
import { showRightBar } from '../../../../../../../store/actions/rightBarActions'
import { TableAdvanced } from '../../../TableAdvanced/TableAdvanced'
import { generateElementID } from '../../../../../../../utils/generateElementID'
import {
    initRuleEmptyData,
    modifyRuleArrayScalarValues,
    modifyRuleCardConfig,
} from '../../../../../../../store/actions/ruleCardActions'
import { ruleCardActions } from '../../../../../../../store/slices/ruleCardSlice'
import { RuleWrappedBlock } from './RuleWrappedBlock'
import { ChipList } from '../../../ChipList/ChipList'
import PromptBtn from '../../../../../../buttons/PromptBtn/PromptBtn'
import { ImportExportDataDropdown } from '../../../../../../overlays/DataImportExportCsv/ImportExportDataDropdown'
import AppButton from '../../../../../../buttons/AppButton/AppButton'
import { DescriptionBtnWithPopup } from '../../../../../../buttons/PromptBtn/DescriptionBtnWithPopup'

interface Props {
    dataPath: string
    pathWithElementID: string | undefined
    data: any
    dataSchema: any
    name: string
    wrapper?: string
    levelDeep?: number
    isViewOnly?: boolean
    element_id?: string
    inCard?: boolean
    isRuleActive?: boolean
    isParentDisabled?: boolean
}

export const RuleChipListBlock = memo(function RuleChipListBlock({
    dataPath,
    pathWithElementID,
    data,
    dataSchema,
    name,
    wrapper,
    levelDeep,
    isViewOnly,
    element_id,
    inCard,
    isRuleActive,
    isParentDisabled,
}: Props) {
    const dispatch = useAppDispatch()
    const isChecked = useAppSelector(selectRuleIsChecked)
    const {
        type,
        name: inputLabel,
        is_required: isRequired,
        is_empty_as_null: isEmptyAsNull,
        is_auxiliary: isCollapsed,
    } = dataSchema

    const validationCheck = inputValidationCheck(type, dataSchema)

    const options = (
        typeof data === 'string'
            ? data.split(' ')
            : Array.isArray(data)
              ? data
              : []
    )
        .map((el: string | number) => ({
            label: String(el),
            value: el,
        }))
        .filter((el: { label: string; value: any }) => el.value)

    const path = useMemo(
        () => (dataPath?.length ? [...dataPath.split(PATH_JOINER), name] : []),
        [dataPath, name]
    )

    const errorKey = pathWithElementID
        ? [...pathWithElementID?.split(PATH_JOINER), name].join(PATH_JOINER)
        : path.join(PATH_JOINER)

    const [inputState, setInputState, touched, setTouched, _, isValid] =
        useFormValidation({ [name]: options }, { [name]: validationCheck })

    const dataLen = inputState?.[name]?.length
    const label = getLabel(name, inputLabel)
    const childSchema = dataSchema?.children || {}
    const childSchemaValidation = inputValidationCheck(
        childSchema.type,
        childSchema
    )
    const desc = dataSchema?.children?.description || dataSchema?.description

    const handleImportSubmit = (data: any[]) => {
        dispatch(modifyRuleCardConfig({ path, data}))
    }

    const handleImportFromCsv = () => {
        const importHandler = (args: CsvDataImporterArgs) => {
            const { dataFromCsv, setData, setError, setErrPosition } = args

            const arrOfStringValues = dataFromCsv.map((item) => item[name])
            const uniqueStrings = Array.from(new Set(arrOfStringValues))

            for (let i = 0; i < uniqueStrings.length; i++) {
                const isValid = childSchemaValidation.isValidSync(
                    uniqueStrings[i]
                )
                if (!isValid) {
                    const err = getErrorsByType(
                        childSchema.type,
                        uniqueStrings[i],
                        childSchema
                    )
                    setError({
                        message: err?.message! || 'field-validation-error',
                        values: err?.values,
                    })
                    setErrPosition({ column: name, row: i + 1 })
                    return
                }
            }
            setData(uniqueStrings)
            setTouched({})
        }

        dispatch(
            showModal(ModalTypes.CSV_MODAL, {
                path,
                objectSchema: { [name]: '' },
                onImportFromCsv: importHandler,
                onImportSubmit: handleImportSubmit,
            })
        )
    }

    const handleExportToCsv = () => {
        const dataToBeCsv = mapToDictArr(data, name)
        const csvFileBlob = exportDataToCsv([name], dataToBeCsv)
        const fileName = path.join('-') + '.csv'
        fileDownload(csvFileBlob, fileName)
    }

    const handleAdvancedSettingsCLick = () => {
        dispatch(
            showRightBar({
                rightBarItem: (
                    <TableAdvanced
                        config={inputState[name].map((el: any) => ({
                            [name]: el.value,
                            element_id: generateElementID(),
                        }))}
                        schema={{
                            ...dataSchema,
                            children: {
                                children: {
                                    [name]: dataSchema.children,
                                },
                            },
                        }}
                        label={label}
                        path={path}
                        itemEditIndex={undefined}
                        scalarsName={name}
                        isRuleCard
                    />
                ),
                disableHide: true,
            })
        )
    }

    useEffect(() => {
        setInputState({ [name]: options })
    }, [data]) // eslint-disable-line

    useEffect(() => {
        if (isChecked) {
            isValid()
        }
    }, [isChecked]) // eslint-disable-line

    useEffect(() => {
        if (dataPath?.length && (!data || !Object.keys(data).length)) {
            dispatch(initRuleEmptyData({ path, name, schema: dataSchema }))
        }
    }, []) // eslint-disable-line

    useEffect(() => {
        if (!data) return
        if (data?.length !== dataLen) {
            dispatch(
                modifyRuleArrayScalarValues({
                    componentState: inputState,
                    path,
                    name,
                    isEmptyAsNull: dataSchema?.is_empty_as_null,
                })
            )
        }
        if (isRequired && isEmptyAsNull && !dataLen) {
            dispatch(
                ruleCardActions.setRuleConfigInvalid({ errorKeyName: errorKey })
            )
        }
        if (isRequired && !isEmptyAsNull && !dataLen) {
            dispatch(
                ruleCardActions.setRuleConfigValid({ errorKeyName: errorKey })
            )
        }
        if (isRequired && isEmptyAsNull && dataLen) {
            dispatch(
                ruleCardActions.setRuleConfigValid({ errorKeyName: errorKey })
            )
        }
    }, [inputState, touched]) // eslint-disable-line

    return (
        <RuleWrappedBlock
            inCard={inCard}
            isRuleActive={isRuleActive}
            name={name}
            isViewOnly={isViewOnly}
            elements={[
                <ChipList
                    name={name}
                    schema={dataSchema}
                    state={inputState}
                    setState={setInputState}
                />,
            ]}
            wrapper={wrapper}
            title={label}
            buttons={[
                <>
                    {isRequired &&
                        isEmptyAsNull &&
                        !dataLen &&
                        !isParentDisabled && (
                            <PromptBtn
                                className="me-2 mt-1"
                                type="validation"
                                prompt="At least 1 item required"
                            />
                        )}
                    {!isViewOnly && (
                        <ImportExportDataDropdown
                            exportToCsv={handleExportToCsv}
                            importFromCsv={handleImportFromCsv}
                        />
                    )}
                    <AppButton
                        variant="advancedSettings"
                        onClick={handleAdvancedSettingsCLick}
                        className="p-1 text-light"
                    />
                    {desc && (
                        <DescriptionBtnWithPopup
                            type="small"
                            header={label}
                            description={desc}
                            className="text-light m-1 z-1000"
                        />
                    )}
                </>,
            ]}
            dataSchema={{
                is_required: true,
                requiredMark: isRequired && isEmptyAsNull,
                is_auxiliary: isCollapsed,
            }}
            dataPath={dataPath}
            levelDeep={levelDeep}
            element_id={element_id}
            pathWithElementID={undefined}
        />
    )
})
